import { create } from "zustand";
import { connectSocket, disconnectSocket, subscribeToDevices, getLastPositionBatch, getSocket } from "../../socket";
import { ref, onValue, off, type DatabaseReference } from "firebase/database";
import { database } from "../../firebaseConfig";
import { getConnectionType } from "../../config/appsConfig";
import { determineVehicleStatus } from "../shared/vehicleStatusRule";

const API_URL = import.meta.env.VITE_FIREBASE_API_URL;

// Helper to map API unit data to UnitData interface
const mapApiUnitToUnitData = (apiUnit: any): UnitData => {
  // Handle different timestamp field names from API
  let lastReport = apiUnit.lastReport || apiUnit.last_report || apiUnit.timestamp || null;
  
  // If lastReport is a Firestore timestamp object, keep it as is
  // If it's a number, keep it as is (will be handled in formatLastReport)
  // If it's an object with _seconds, keep it as is
  
  return {
    id: apiUnit.id || apiUnit.device_id || "",
    name: apiUnit.name || apiUnit.vehicle_name || "Sin nombre",
    latitude: apiUnit.latitude ?? apiUnit.lat ?? 0,
    longitude: apiUnit.longitude ?? apiUnit.lon ?? apiUnit.long ?? 0,
    status: apiUnit.status || "offline",
    speed: apiUnit.speed ?? 0,
    lastReport: lastReport,
    address: apiUnit.address || null,
    coordinates: apiUnit.coordinates || null,
    odometer: apiUnit.odometer ?? apiUnit.odo_km ?? 0,
    ignition: apiUnit.ignition ?? false,
    course: apiUnit.course ?? apiUnit.heading ?? 0,
  };
};

export interface UnitData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  status: string;
  speed: number;
  lastReport: any;
  address: string | null;
  coordinates: string | null;
  odometer: number;
  ignition: boolean;
  course: number;
  idleSince?: number | null;
}

export interface ActiveUser {
  id: string;
  guestNumber: number;
  joinedAt: any;
  lastSeen: any;
}

export interface PublicTrackingLinkData {
  name: string;
  units: UnitData[];
  activeUsers: ActiveUser[];
  expirationDate: any;
  company_id: string;
}

export type LinkStatus =
  | "loading"
  | "access_required"
  | "active"
  | "not_found"
  | "expired"
  | "inactive"
  | "pending"
  | "invalid_code"
  | "error";

interface PublicTrackingLinkState {
  linkData: PublicTrackingLinkData | null;
  status: LinkStatus;
  errorMessage: string | null;
  loading: boolean;
  isSocketConnected: boolean;
  currentHash: string | null;
  currentAccessCode: string | null;

  // Actions
  fetchPublicLink: (hash: string, accessCode?: string) => Promise<void>;
  startRealTimeUpdates: (hash: string, accessCode?: string) => Promise<void>;
  stopRealTimeUpdates: () => void;
  reset: () => void;
}

// Tiempo de reconexión WebSocket: 55 minutos (antes de que expire la hora del socket).
// RDB no necesita reconexión manual: Firebase mantiene la suscripción viva.
const RECONNECT_INTERVAL = 55 * 60 * 1000;

// Detecta movimiento real (>~20m). Igual semantic que vehiclesSocketStore.
const MOVEMENT_THRESHOLD = 0.0002;
const hasRealMovement = (
  prevLat: number,
  prevLon: number,
  newLat: number,
  newLon: number,
): boolean => {
  if (prevLat === 0 && prevLon === 0) return false;
  return (
    Math.abs(newLat - prevLat) > MOVEMENT_THRESHOLD ||
    Math.abs(newLon - prevLon) > MOVEMENT_THRESHOLD
  );
};

// Payload normalizado que cualquier transporte (WS/RDB) produce hacia el aplicador
interface NormalizedPositionUpdate {
  lat?: number | null;
  lon?: number | null;
  speed?: number | null;
  timeStamp?: number | null;
  heading?: number | null;
  address?: string | null;
  engineStatus?: number | null;
  odoKm?: number | null;
}

export const usePublicTrackingLinkStore = create<PublicTrackingLinkState>(
  (set, get) => {
    let reconnectInterval: ReturnType<typeof setInterval> | null = null;
    let rdbListenerRefs: DatabaseReference[] = [];

    // Aplica un update normalizado a la unidad correspondiente. Único punto donde
    // se calcula el nuevo status — así WS y RDB comparten la misma lógica.
    const applyPositionUpdate = (
      deviceId: string,
      payload: NormalizedPositionUpdate,
    ) => {
      const currentState = get();
      if (!currentState.linkData?.units) return;
      if (payload.lat == null && payload.lon == null) return;

      const updatedUnits = currentState.linkData.units.map(unit => {
        if (unit.id !== deviceId) return unit;

        const newLat = payload.lat ?? unit.latitude;
        const newLon = payload.lon ?? unit.longitude;
        const movementDetected = hasRealMovement(
          unit.latitude, unit.longitude, newLat, newLon,
        );

        const ignition = payload.engineStatus != null
          ? (payload.engineStatus === 1 || payload.engineStatus === 2 || payload.engineStatus === 3)
          : unit.ignition;

        const { status: newStatus, idleSince: newIdleSince } =
          determineVehicleStatus(
            payload.speed,
            ignition,
            payload.timeStamp,
            unit.status,
            movementDetected,
            unit.idleSince,
          );

        return {
          ...unit,
          latitude: newLat,
          longitude: newLon,
          // speed: respetar null explícito (mismo criterio que vehiclesSocketStore)
          speed: payload.speed !== undefined ? (payload.speed ?? 0) : unit.speed,
          status: newStatus,
          idleSince: newIdleSince,
          lastReport: payload.timeStamp ?? unit.lastReport,
          ignition,
          odometer: payload.odoKm ?? unit.odometer,
          address: payload.address ?? unit.address,
          // heading: preservar último conocido si llega null/undefined (igual que vehiclesSocketStore)
          course: payload.heading != null && payload.heading !== 0 ? payload.heading : unit.course,
        };
      });

      set({
        linkData: { ...currentState.linkData, units: updatedUnits },
      });
    };

    // ── WebSocket transport ─────────────────────────────────────────────────
    const setupSocketListeners = (deviceIds: string[]) => {
      const socket = getSocket();
      socket.removeAllListeners();

      const subscribeAndFetch = () => {
        subscribeToDevices(deviceIds);
        if (deviceIds.length > 0) {
          setTimeout(() => {
            getLastPositionBatch(deviceIds, false);
          }, 100);
        }
      };

      if (socket.connected) {
        set({ isSocketConnected: true });
        subscribeAndFetch();
      }

      socket.on("connect", () => {
        set({ isSocketConnected: true });
        subscribeAndFetch();
      });

      socket.on("disconnect", () => {
        set({ isSocketConnected: false });
      });

      socket.on("connect_error", () => {
        set({ isSocketConnected: false });
      });

      socket.on("position", (position: any) => {
        const point = position?.point;
        if (!position?.device_id || !point) return;
        applyPositionUpdate(position.device_id, {
          lat: point.lat,
          lon: point.lon,
          speed: point.speed,
          timeStamp: point.timeStamp,
          heading: point.heading,
          address: point.address?.label,
          engineStatus: point.params?.engine_status,
          odoKm: point.params?.odo_km,
        });
      });

      socket.on("last_position", (position: any) => {
        const point = position?.point;
        const deviceId = position?.device_id ?? point?.device_id;
        if (!deviceId || !point) return;
        applyPositionUpdate(deviceId, {
          lat: point.lat,
          lon: point.lon,
          speed: point.speed,
          timeStamp: point.timeStamp,
          heading: point.heading,
          address: point.address?.label,
          engineStatus: point.params?.engine_status,
          odoKm: point.params?.odo_km,
        });
      });

      socket.on("event", (eventData: any) => {
        const event = eventData?.event;
        const deviceId = eventData?.device_id ?? event?.device_id;
        if (!deviceId || !event) return;
        applyPositionUpdate(deviceId, {
          lat: event.lat,
          lon: event.lon,
          timeStamp: event.timeStamp,
          engineStatus: event.params?.engine_status,
        });
      });
    };

    // ── Firebase RDB transport ──────────────────────────────────────────────
    // Una suscripción por unidad a positions/{companyId}/{deviceId}. RDB ya
    // gestiona reconexión y heartbeats, no necesitamos el reconnect interval.
    const setupRdbListeners = (units: UnitData[], companyId: string) => {
      if (!companyId) {
        console.warn("[PublicTracking RDB] linkData sin company_id, no se puede suscribir");
        set({ isSocketConnected: false });
        return;
      }

      set({ isSocketConnected: true });

      units.forEach(unit => {
        const deviceRef = ref(database, `positions/${companyId}/${unit.id}`);
        rdbListenerRefs.push(deviceRef);

        onValue(
          deviceRef,
          (snapshot) => {
            const pos = snapshot.val();
            if (!pos) return;
            applyPositionUpdate(unit.id, {
              lat: pos.lat,
              lon: pos.lon,
              speed: pos.speed,
              timeStamp: pos.timeStamp,
              heading: pos.heading,
              engineStatus: pos.params?.engine_status,
              odoKm: pos.params?.odo_km,
            });
          },
          (error) => {
            console.error(`[PublicTracking RDB] Error en device ${unit.id}:`, error.message);
            set({ isSocketConnected: false });
          },
        );
      });
    };

    const teardownRdbListeners = () => {
      rdbListenerRefs.forEach(r => off(r));
      rdbListenerRefs = [];
    };

    return {
      linkData: null,
      status: "loading",
      errorMessage: null,
      loading: false,
      isSocketConnected: false,
      currentHash: null,
      currentAccessCode: null,

      fetchPublicLink: async (hash: string, accessCode?: string) => {
      set({ loading: true, errorMessage: null });

      try {
        let url = `${API_URL}/trackingLinks/public?hash=${hash}`;
        if (accessCode) {
          url += `&accessCode=${encodeURIComponent(accessCode)}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();

        if (response.ok) {
          // API response structure: { data: { message, data: { name, units, ... } } }
          const linkInfo = result.data?.data || result.data;
          
          // Debug: Verificar los datos recibidos
          
          // Asegurar que activeUsers sea siempre un array
          const safeActiveUsers = Array.isArray(linkInfo.activeUsers) ? linkInfo.activeUsers : [];
          
          // Map API units to UnitData interface
          const mappedUnits = Array.isArray(linkInfo.units) 
            ? linkInfo.units.map(mapApiUnitToUnitData) 
            : [];
          
          set({
            linkData: {
              name: linkInfo.name,
              units: mappedUnits,
              activeUsers: safeActiveUsers,
              expirationDate: linkInfo.expirationDate,
              company_id: linkInfo.company_id,
            },
            status: "active",
            loading: false,
          });
        } else {
          // Handle specific error codes - API returns { meta: { message } }
          const errorCode =
            result.meta?.message || result.error || result.message;

          if (response.status === 401) {
            // Check for access code required (handle translated messages too)
            if (
              errorCode === "accessCodeRequired" ||
              errorCode?.includes("código de acceso") ||
              errorCode?.includes("access code")
            ) {
              set({ status: "access_required", loading: false });
            } else if (
              errorCode === "invalidAccessCode" ||
              errorCode?.includes("incorrecto") ||
              errorCode?.includes("invalid")
            ) {
              set({
                status: "invalid_code",
                errorMessage: "Código de acceso incorrecto",
                loading: false,
              });
            } else {
              // Default 401 - assume access required
              set({ status: "access_required", loading: false });
            }
          } else if (response.status === 404) {
            set({
              status: "not_found",
              loading: false,
            });
          } else if (response.status === 403) {
            if (
              errorCode === "trackingLinkInactive" ||
              errorCode?.includes("inactivo")
            ) {
              set({ status: "inactive", loading: false });
            } else if (
              errorCode === "trackingLinkNotYetActive" ||
              errorCode?.includes("aún no")
            ) {
              set({ status: "pending", loading: false });
            } else {
              set({ status: "inactive", loading: false });
            }
          } else if (response.status === 410) {
            set({ status: "expired", loading: false });
          } else {
            set({
              status: "error",
              errorMessage: "Error al cargar el link",
              loading: false,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching public tracking link:", error);
        set({
          status: "error",
          errorMessage: "Error de conexión",
          loading: false,
        });
      }
    },

    startRealTimeUpdates: async (hash: string, accessCode?: string) => {
      // Stop any existing connection
      get().stopRealTimeUpdates();

      set({ currentHash: hash, currentAccessCode: accessCode });

      // Initial fetch para obtener units + company_id
      await get().fetchPublicLink(hash, accessCode);

      const state = get();
      if (state.status !== "active" || !state.linkData?.units) return;

      const transport = getConnectionType();
      const deviceIds = state.linkData.units.map(unit => unit.id);

      if (transport === "rdb") {
        // RDB: suscripciones por device + Firebase maneja reconexión
        setupRdbListeners(state.linkData.units, state.linkData.company_id);
      } else {
        // WebSocket: conectar + listeners + reconnect cada 55min
        connectSocket();
        setupSocketListeners(deviceIds);

        reconnectInterval = setInterval(() => {
          const currentState = get();
          const currentDeviceIds = currentState.linkData?.units?.map(u => u.id) || [];
          disconnectSocket();
          setTimeout(() => {
            connectSocket();
            setupSocketListeners(currentDeviceIds);
          }, 1000);
        }, RECONNECT_INTERVAL);
      }
    },

    stopRealTimeUpdates: () => {
      // Clear reconnect interval (WS)
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
        reconnectInterval = null;
      }

      // Limpiar AMBOS transportes — barato y seguro ante cambios de transport
      // o llamadas duplicadas. Cada teardown es no-op si nunca se inicializó.
      teardownRdbListeners();

      const socket = getSocket();
      socket.removeAllListeners();
      disconnectSocket();

      set({ isSocketConnected: false });
    },

    reset: () => {
      get().stopRealTimeUpdates();

      set({
        linkData: null,
        status: "loading",
        errorMessage: null,
        loading: false,
        isSocketConnected: false,
        currentHash: null,
        currentAccessCode: null,
      });
    },
    };
  });
